import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, initializeAndCheckSession } from '../lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';

export interface UserProfile {
  id: string;
  username: string;
  avatar_url: string;
  created_at?: string;
  updated_at?: string;
  age?: number;
  description?: string;
  theme?: 'dark' | 'retro' | 'neon';
}

export interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  loadingProfile: boolean;
  updateProfile: (profile: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasProfile: boolean;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  loadingProfile: true,
  updateProfile: async () => {},
  refreshProfile: async () => {},
  hasProfile: false,
  logout: async () => {},
  setUser: () => {},
});

// Función auxiliar para verificar si un error es de autenticación
const isAuthError = (error: PostgrestError): boolean => {
  return error.code === '401' || error.code === 'PGRST301' || error.message?.includes('JWT');
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Cargar el perfil del usuario desde la base de datos
  const fetchProfile = async (userId: string) => {
    setLoadingProfile(true);
    try {
      let attempt = 0;
      let data: any = null;
      let error: PostgrestError | null = null;
      
      // Intentar con reintentos
      while (attempt < MAX_RETRIES && !data) {
        console.log(`Intento ${attempt + 1}/${MAX_RETRIES} de cargar perfil para ID: ${userId}`);
        
        // Ya no es necesario obtener la API key aquí, confiamos en el cliente global
        // const currentApiKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'FALLBACK_KEY_SI_ES_NECESARIO';
        // console.log(`Usando API key: ${currentApiKey.substring(0,10)}... para fetchProfile`);
        
        try {
          // Llamada directa usando el cliente supabase configurado globalmente
          const response = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single()
            .throwOnError(); // Lanzar error si la petición falla (incluyendo 401)
          
          data = response.data;
          error = response.error; // Mantenido por si throwOnError no captura algo
        
        } catch (fetchError: any) {
          console.log(`Error en intento ${attempt + 1} (${fetchError.code || fetchError.name}): ${fetchError.message}`);
          error = fetchError; // Asignar el error capturado
          
          // Si es un error de autenticación (401), intentar refrescar la sesión
          if (fetchError.status === 401 || fetchError.code === 'PGRST301') {
            console.log('Error 401 detectado, intentando refreshSession...');
            try {
              const { error: refreshError } = await supabase.auth.refreshSession();
              if (refreshError) {
                console.error('Error al refrescar sesión:', refreshError);
                if (attempt === MAX_RETRIES - 1) {
                  console.error('Fallo final al cargar perfil después de refresh fallido:', error);
                  window.dispatchEvent(new CustomEvent('supabase-auth-error', { 
                    detail: { message: 'Error de sesión. Por favor, inicia sesión de nuevo.' }
                  }));
                  setProfile(null);
                  setLoadingProfile(false);
                  return;
                }
              } else {
                console.log('Sesión refrescada correctamente, reintentando cargar perfil en el siguiente ciclo.');
              }
            } catch (refreshException) {
              console.error('Excepción al intentar refrescar sesión:', refreshException);
            }
          } else {
            console.error(`Otro tipo de error (${fetchError.status || 'sin estado'}) al cargar perfil:`, fetchError);
          }
        }
        
        // Lógica de espera y reintento
        if (!data && attempt < MAX_RETRIES - 1) {
          console.log(`Esperando ${1000 * (attempt + 1)}ms antes de reintentar...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          attempt++;
        } else if (!data) {
          attempt = MAX_RETRIES; // Salir del bucle si no hay datos después del último intento
        }
      } // Fin del while
      
      // Procesamiento final después de los reintentos
      if (!data) {
        // Si no se encontró perfil, verificar si el error fue por no encontrarlo o por otra cosa
        if (error && (error.code === 'PGRST116' || error.message?.includes('Result contains 0 rows'))) { 
          console.log('No se encontró perfil para el usuario, creando uno nuevo');
          // Crear un perfil por defecto
          const newProfile = {
            id: userId,
            username: user?.email?.split('@')[0] || 'usuario',
            avatar_url: null,
            theme: 'dark',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          // Intentar crear perfil con reintentos
          attempt = 0;
          let createdProfile: any = null;
          let createError: PostgrestError | null = null;
          
          while (attempt < MAX_RETRIES && !createdProfile) {
            try {
              console.log(`Intento ${attempt + 1}/${MAX_RETRIES} de crear perfil`);
              const response = await supabase
                .from('profiles')
                .upsert(newProfile)
                .select()
                .single();
                
              createdProfile = response.data;
              createError = response.error;
              
              if (createError) {
                console.error(`Error en intento ${attempt + 1} al crear perfil:`, createError);
                // Si es error de autenticación, intentar refresh
                if (isAuthError(createError)) {
                  await supabase.auth.refreshSession();
                }
                
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
                attempt++;
                continue;
              }
            } catch (err) {
              console.error(`Excepción en intento ${attempt + 1} al crear perfil:`, err);
              await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
              attempt++;
              continue;
            }
          }

          if (createdProfile) {
            setProfile(createdProfile);
          } else {
            console.error('Error al crear perfil inicial después de múltiples intentos:', createError);
            setProfile(null);
          }
        } else {
          // Si el error fue otro (ej. 401 persistente)
          console.error('Error final al cargar el perfil después de múltiples intentos:', error);
          // Disparar evento de error de autenticación
          window.dispatchEvent(new CustomEvent('supabase-auth-error', {
            detail: { message: 'No se pudo cargar tu perfil. Intenta iniciar sesión de nuevo.' }
          }));
          setProfile(null);
        }
      } else {
        // Perfil cargado exitosamente
        setProfile(data);
      }
    } catch (error) {
      console.error('Error inesperado al cargar el perfil:', error);
      setProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Actualizar el perfil del usuario
  const updateProfile = async (profileData: Partial<UserProfile>) => {
    if (!user) throw new Error('No hay usuario autenticado');

    try {
      // Intentar con reintentos
      let attempt = 0;
      let existingProfile: any[] | null = null;
      let fetchError: PostgrestError | null = null;
      
      // Primero verificar si el perfil existe
      while (attempt < MAX_RETRIES && existingProfile === null) {
        try {
          console.log(`Intento ${attempt + 1}/${MAX_RETRIES} de verificar perfil existente`);
          const response = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id);
            
          existingProfile = response.data;
          fetchError = response.error;
          
          if (fetchError) {
            // Si es error de autenticación, intentar refresh
            if (isAuthError(fetchError)) {
              console.log('Error de autenticación al verificar perfil, intentando refreshSession');
              await supabase.auth.refreshSession();
            } else if (fetchError.code !== 'PGRST116') {
              console.error(`Error en intento ${attempt + 1} al verificar perfil:`, fetchError);
            }
            
            if (attempt < MAX_RETRIES - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
              attempt++;
              continue;
            }
          }
        } catch (err) {
          console.error(`Excepción en intento ${attempt + 1} al verificar perfil:`, err);
          if (attempt < MAX_RETRIES - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            attempt++;
            continue;
          }
          throw err;
        }
      }
      
      // Preparar los datos para insertar o actualizar
      const updates = {
        id: user.id,
        // Si el perfil no existe y no se proporciona un nombre de usuario, usar el email como nombre de usuario temporal
        username: existingProfile && !existingProfile.length && !profileData.username 
          ? user.email?.split('@')[0] || 'usuario' 
          : profileData.username,
        ...profileData,
        updated_at: new Date().toISOString(),
      };

      // Intentar actualizar con reintentos
      attempt = 0;
      let updatedProfile: any = null;
      let updateError: PostgrestError | null = null;
      
      while (attempt < MAX_RETRIES && !updatedProfile) {
        try {
          console.log(`Intento ${attempt + 1}/${MAX_RETRIES} de actualizar perfil`);
          const response = await supabase
            .from('profiles')
            .upsert(updates, { onConflict: 'id' })
            .select()
            .single();
            
          updatedProfile = response.data;
          updateError = response.error;
          
          if (updateError) {
            // Si es error de autenticación, intentar refresh
            if (isAuthError(updateError)) {
              console.log('Error de autenticación al actualizar perfil, intentando refreshSession');
              await supabase.auth.refreshSession();
            } else {
              console.error(`Error en intento ${attempt + 1} al actualizar perfil:`, updateError);
            }
            
            if (attempt < MAX_RETRIES - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
              attempt++;
              continue;
            } else {
              throw updateError;
            }
          }
        } catch (err) {
          console.error(`Excepción en intento ${attempt + 1} al actualizar perfil:`, err);
          if (attempt < MAX_RETRIES - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            attempt++;
            continue;
          }
          throw err;
        }
      }

      if (updatedProfile) {
        // Actualizar el estado con los datos devueltos por la API
        setProfile(updatedProfile);
        console.log('Perfil actualizado con éxito:', updatedProfile);
      } else {
        throw updateError || new Error('No se pudo actualizar el perfil después de múltiples intentos');
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      throw error;
    }
  };

  // Refrescar los datos del perfil
  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  // Cerrar sesión
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      // No es necesario limpiar el estado aquí porque los efectos
      // detectarán el cambio en el estado de autenticación
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Inicializar y verificar la sesión al cargar el componente
    const initAuth = async () => {
      try {
        // Verificar la sesión con la nueva función
        await initializeAndCheckSession();
        
        // Obtener sesión actual
        const { data: { session } } = await supabase.auth.getSession();
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        
        if (currentUser) {
          fetchProfile(currentUser.id);
        } else {
          setProfile(null);
          setLoadingProfile(false);
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Escuchar cambios en la autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      
      if (currentUser) {
        await fetchProfile(currentUser.id);
      } else {
        setProfile(null);
        setLoadingProfile(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const hasProfile = !!profile && !!profile.username;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        loadingProfile,
        updateProfile,
        refreshProfile,
        hasProfile,
        logout,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 